/**
 * CaretMapBuilder - Utilities for building and adjusting caret maps
 *
 * Extracted from EdgeDocsCanvas.V3.tsx as part of Phase 3 Step 6.
 *
 * Caret maps provide position information for cursor placement. This module
 * handles collecting caret entries from multiple snapshots, filtering by
 * grapheme range, sorting, and adjusting Y positions for page-relative rendering.
 */

import { InputLogger } from '../../../../../../utils/loggers/InputLogger';

/**
 * Base interface for caret map entry - requires minimal position info
 * Using generics to preserve the exact type passed in
 */
export interface CaretMapEntryBase {
  grapheme_index: number;
  x_fp: number;
  y_fp: number;
}

/**
 * Base interface for snapshot with caret map
 */
export interface SnapshotWithCaretMapBase<T extends CaretMapEntryBase> {
  caret_map?: T[];
}

/**
 * A line with grapheme index information
 */
export interface LineWithGraphemeRange {
  start_grapheme_index: number;
  end_grapheme_index: number;
}

/**
 * Options for collecting caret map entries
 */
export interface CaretMapCollectionOptions {
  /** Page index for logging */
  pageIndex?: number;
  /** Enable console logging */
  enableLogging?: boolean;
  /** Handle empty lines at end of document */
  handleEmptyLines?: boolean;
}

/**
 * Options for adjusting caret Y positions
 */
export interface CaretYAdjustmentOptions {
  /** Page index (adjustment only applies to pages > 0) */
  pageIndex: number;
  /** Y offset to subtract from caret positions */
  pageYOffset: number;
  /** Enable console logging */
  enableLogging?: boolean;
}

/**
 * Result of caret map collection
 */
export interface CaretMapCollectionResult<T extends CaretMapEntryBase> {
  /** Collected and sorted caret map entries */
  caretMap: T[];
  /** Number of entries collected */
  entryCount: number;
  /** Grapheme range covered */
  graphemeRange: { start: number; end: number } | null;
}

/**
 * Collects caret map entries from multiple snapshots for a specific page's grapheme range
 *
 * This function:
 * 1. Determines the grapheme range from the page's lines
 * 2. Filters caret entries from all snapshots that fall within the range
 * 3. Handles edge case of empty lines at end of document
 * 4. Sorts entries by grapheme_index
 *
 * @param snapshots - Array of snapshots to collect from
 * @param pageLines - Lines on the current page (to determine grapheme range)
 * @param options - Collection options
 * @returns Collected and sorted caret map entries
 *
 * @example
 * ```typescript
 * const result = collectCaretMapEntries(cachedSnapshots, pageLines, {
 *   pageIndex: 0,
 *   enableLogging: true,
 *   handleEmptyLines: true
 * });
 * ```
 */
export function collectCaretMapEntries<T extends CaretMapEntryBase>(
  snapshots: Array<{ caret_map?: T[] }>,
  pageLines: LineWithGraphemeRange[],
  options: CaretMapCollectionOptions = {}
): CaretMapCollectionResult<T> {
  const { pageIndex = 0, enableLogging = false, handleEmptyLines = true } = options;

  if (pageLines.length === 0) {
    return {
      caretMap: [],
      entryCount: 0,
      graphemeRange: null
    };
  }

  const pageStartGrapheme = pageLines[0].start_grapheme_index;
  const pageEndGrapheme = pageLines[pageLines.length - 1].end_grapheme_index;
  
  // Check for empty line at end (where start == end)
  const lastLine = pageLines[pageLines.length - 1];
  const lastLineIsEmpty = handleEmptyLines && 
    lastLine.start_grapheme_index === lastLine.end_grapheme_index;

  const collectedEntries: T[] = [];

  // Collect caret entries from all snapshots that fall within page bounds
  for (const snapshot of snapshots) {
    if (snapshot?.caret_map) {
      const relevantEntries = snapshot.caret_map.filter(entry => {
        const inRange = entry.grapheme_index >= pageStartGrapheme && 
                       entry.grapheme_index <= pageEndGrapheme;
        
        // Also include caret at the empty line's start position (which equals end)
        // This handles the "end of document" caret position
        const atEmptyLineStart = lastLineIsEmpty && 
          entry.grapheme_index === lastLine.start_grapheme_index;
        
        return inRange || atEmptyLineStart;
      });
      collectedEntries.push(...relevantEntries);
    }
  }

  // Sort by grapheme_index, then by x_fp (lower x first for consistent behavior)
  // This ensures that when deduplicating, we keep the leftmost caret position
  collectedEntries.sort((a, b) => {
    if (a.grapheme_index !== b.grapheme_index) {
      return a.grapheme_index - b.grapheme_index;
    }
    return a.x_fp - b.x_fp; // Lower x_fp first
  });

  // 🔥 FIX (2026-01-19): Deduplicate by grapheme_index, keeping the first (leftmost) entry
  // When pressing ENTER, both the ending paragraph and the new empty paragraph have
  // caret entries at the boundary grapheme index. Without deduplication, find() may
  // return the wrong entry (the one from the ending paragraph with x after the last char)
  // instead of the correct one (left margin from the new paragraph).
  // This mirrors the deduplication logic in WASM caret_map.rs
  const deduplicatedEntries: T[] = [];
  let lastGraphemeIndex = -1;
  for (const entry of collectedEntries) {
    if (entry.grapheme_index !== lastGraphemeIndex) {
      deduplicatedEntries.push(entry);
      lastGraphemeIndex = entry.grapheme_index;
    }
    // Skip duplicate entries (they have same grapheme_index but higher x_fp due to sort)
  }

  if (enableLogging) {
    InputLogger.caretDiag.mapBuilt(pageIndex, {
      pageStartGrapheme,
      pageEndGrapheme,
      lastLineIsEmpty,
      lastLineRange: `[${lastLine.start_grapheme_index}, ${lastLine.end_grapheme_index})`,
      extractedCaretEntries: deduplicatedEntries.length,
      caretIndices: deduplicatedEntries.slice(-5).map(c => c.grapheme_index)
    });
  }

  return {
    caretMap: deduplicatedEntries,
    entryCount: deduplicatedEntries.length,
    graphemeRange: { start: pageStartGrapheme, end: pageEndGrapheme }
  };
}

/**
 * Adjusts caret Y positions for page-relative rendering
 *
 * For pages > 0, caret positions have absolute Y values from the document start.
 * This function adjusts them to be relative to the page's top margin.
 *
 * @param caretMap - Array of caret entries to adjust (will NOT be mutated)
 * @param options - Adjustment options including pageYOffset
 * @returns New array with adjusted Y positions
 *
 * @example
 * ```typescript
 * const adjustedCaretMap = adjustCaretYPositions(pageCaretMap, {
 *   pageIndex: 1,
 *   pageYOffset: 64 * 792, // One page height in fp units
 *   enableLogging: true
 * });
 * ```
 */
export function adjustCaretYPositions<T extends CaretMapEntryBase>(
  caretMap: T[],
  options: CaretYAdjustmentOptions
): T[] {
  const { pageIndex, pageYOffset, enableLogging = false } = options;

  // Only adjust for pages > 0 and non-zero offset
  if (pageIndex === 0 || pageYOffset === 0 || caretMap.length === 0) {
    return caretMap;
  }

  const adjusted = caretMap.map(entry => ({
    ...entry,
    y_fp: (entry.y_fp ?? 0) - pageYOffset
  }));

  if (enableLogging) {
    InputLogger.caretDiag.mapBuilt(pageIndex, {
      pageStartGrapheme: 0,
      pageEndGrapheme: 0,
      lastLineIsEmpty: false,
      lastLineRange: 'N/A (Y adjustment)',
      extractedCaretEntries: adjusted.length,
      caretIndices: adjusted.slice(-5).map(c => c.grapheme_index)
    });
  }

  return adjusted;
}

/**
 * Builds a complete caret map for a page by collecting and adjusting entries
 *
 * This is a convenience function that combines collectCaretMapEntries and
 * adjustCaretYPositions into a single operation.
 *
 * @param snapshots - Array of snapshots to collect from
 * @param pageLines - Lines on the current page
 * @param pageIndex - Current page index
 * @param pageYOffset - Y offset for page-relative positioning
 * @param enableLogging - Enable console logging
 * @returns Adjusted caret map ready for rendering
 *
 * @example
 * ```typescript
 * const caretMap = buildPageCaretMap(
 *   cachedSnapshots,
 *   pageLines,
 *   pageIndex,
 *   pageYOffset,
 *   DEBUG_ENABLED
 * );
 * ```
 */
export function buildPageCaretMap<T extends CaretMapEntryBase>(
  snapshots: Array<{ caret_map?: T[] }>,
  pageLines: LineWithGraphemeRange[],
  pageIndex: number,
  pageYOffset: number,
  enableLogging: boolean = false
): T[] {
  // Step 1: Collect caret entries for this page's grapheme range
  const { caretMap } = collectCaretMapEntries(snapshots, pageLines, {
    pageIndex,
    enableLogging,
    handleEmptyLines: true
  });

  // Step 2: Adjust Y positions for page-relative rendering
  return adjustCaretYPositions(caretMap, {
    pageIndex,
    pageYOffset,
    enableLogging
  });
}
